import { useQuery } from '@tanstack/react-query';

import type { GetMarkInTransitToBuyerResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get in transit to buyer status from a purchased vehicle
 */
export const getMarkInTransitToBuyer = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkInTransitToBuyerResponse> => {
	const response = await apiClient.get<GetMarkInTransitToBuyerResponse>(
		ordersEndpoints.markInTransitToBuyer(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting in transit to buyer status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkInTransitToBuyer = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markInTransitToBuyer.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get in transit to buyer status without an order ID.');
			}
			return getMarkInTransitToBuyer(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
